import React from 'react';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';

const COLORS = Array.from({ length: 48 }, (_, index) => {
    const hue = Math.round((index * 137.508) % 360);
    const saturation = [46, 54, 42, 50][index % 4];
    const lightness = [46, 52, 58][index % 3];

    return `hsla(${hue}, ${saturation}%, ${lightness}%, 0.8)`;
});

const buildChartItems = (data) => {
    const sanitizedData = Array.isArray(data)
        ? data.filter((item) => (Number(item?.value) || 0) > 0)
        : [];

    const total = sanitizedData.reduce((sum, item) => sum + (Number(item?.value) || 0), 0);
    
    return sanitizedData.map((item, index) => ({
        ...item,
        color: COLORS[index % COLORS.length],
        percentage: total > 0 ? ((Number(item?.value) || 0) / total) * 100 : 0,
    })).sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", "pt-BR", { sensitivity: "base" }));
};

const formatPercentage = (percentage) => {
    if (!Number.isFinite(percentage) || percentage <= 0) {
        return "0%";
    }

    if (percentage < 0.1) {
        return "<0.1%";
    }

    if (percentage >= 10) {
        return `${percentage.toFixed(0)}%`;
    }

    return `${percentage.toFixed(1).replace(/\.0$/, "")}%`;
};

const HIDDEN_PERCENTAGE_LABEL = "****%";

const CustomItem = ({ data, isDarkMode, valuesHidden = false }) => {
    const chartData = valuesHidden
        ? (Array.isArray(data) ? data.map((item) => ({ ...item, value: 1 })) : [])
        : data;
    const chartItems = buildChartItems(chartData);
    const labelColor = isDarkMode ? "#ffffffcc" : "#344767";
    const subtleLabelColor = isDarkMode ? "#ffffff99" : "#67748e";

    if (chartItems.length === 0) {
        return null;
    }

    return (
        <div
            style={{
                width: '100%',
                padding: '0 16px 16px',
            }}
        >
            <div style={{ width: '100%', height: 'clamp(300px, 70vw, 440px)' }}>
                <ResponsiveContainer>
                    <PieChart>
                        <Pie
                            dataKey="value"
                            data={chartItems}
                            fill="#8884d8"
                            label={false}
                            labelLine={false}
                            paddingAngle={1}
                        >
                            {chartItems.map((entry) => (
                                <Cell key={`cell-${entry.name}`} fill={entry.color} />
                            ))}
                        </Pie>
                        <Tooltip
                            formatter={(_, __, item) =>
                                valuesHidden
                                    ? HIDDEN_PERCENTAGE_LABEL
                                    : formatPercentage(item?.payload?.percentage)
                            }
                            labelFormatter={(_, payload) => payload?.[0]?.payload?.name ?? ""}
                            contentStyle={{
                                borderRadius: 12,
                                border: `1px solid ${isDarkMode ? "#4b5563" : "#dce1ea"}`,
                                backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
                                color: labelColor,
                            }}
                            itemStyle={{ color: labelColor }}
                            labelStyle={{ color: labelColor, fontWeight: 600 }}
                        />
                    </PieChart>
                </ResponsiveContainer>
            </div>

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                    gap: 12,
                    marginTop: 4,
                }}
            >
                
                {chartItems.map((item) => (
                    <div
                        key={`legend-${item.name}`}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            minWidth: 0,
                            padding: '10px 12px',
                            borderRadius: 12,
                            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : '#f8f9fa',
                        }}
                        title={`${item.name} ${valuesHidden ? HIDDEN_PERCENTAGE_LABEL : formatPercentage(item.percentage)}`}
                    >
                        <span
                            style={{
                                flexShrink: 0,
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: item.color,
                            }}
                        />
                        <span
                            style={{
                                flex: 1,
                                minWidth: 0,
                                fontSize: 13,
                                color: labelColor,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {item.name}
                        </span>
                        <span
                            style={{
                                flexShrink: 0,
                                fontSize: 13,
                                fontWeight: 700,
                                color: subtleLabelColor,
                            }}
                        >
                            {valuesHidden ? HIDDEN_PERCENTAGE_LABEL : formatPercentage(item.percentage)}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default CustomItem;
